import { authFetch } from '../utils/authFetch';

const API_BASE = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

export interface Note {
  id: string;
  organization_id: string;
  entity_type: 'customer' | 'invoice';
  entity_id: string;
  content: string;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  user_name?: string;
  administrator_name?: string;
}

export interface NoteCreateRequest {
  entity_type: 'customer' | 'invoice';
  entity_id: string;
  content: string;
  is_private?: boolean;
}

export interface NoteUpdateRequest {
  content: string;
  is_private?: boolean;
}

export interface NotesListResponse {
  total: number;
  notes: Note[];
}

export interface NotesCountResponse {
  count: number;
}

export const notesService = {
  // Create a new note
  async createNote(
    noteData: NoteCreateRequest,
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Note> {
    const response = await authFetch(
      `${API_BASE}/notes?organization_id=${organizationId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      },
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to create note');
    }

    const result = await response.json();
    return result.data;
  },

  // Get notes for a specific entity (customer or invoice)
  async getNotesByEntity(
    entityType: 'customer' | 'invoice',
    entityId: string,
    organizationId: string,
    includePrivate: boolean = false,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<NotesListResponse> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (includePrivate) {
      params.append('include_private', 'true');
    }

    const response = await authFetch(
      `${API_BASE}/notes/entity/${entityType}/${entityId}?${params.toString()}`,
      {},
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to fetch notes');
    }

    const result = await response.json();
    return result.data;
  },


  // Get a specific note by ID
  async getNoteById(
    noteId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Note> {
    const response = await authFetch(
      `${API_BASE}/notes/${noteId}`,
      {},
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to fetch note');
    }

    const result = await response.json();
    return result.data;
  },

  // Update an existing note
  async updateNote(
    noteId: string,
    noteData: NoteUpdateRequest,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<Note> {
    const response = await authFetch(
      `${API_BASE}/notes/${noteId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noteData),
      },
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to update note');
    }

    const result = await response.json();
    return result.data;
  },

  // Delete a note
  async deleteNote(
    noteId: string,
    organizationId: string,
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<boolean> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);

    const response = await authFetch(
      `${API_BASE}/notes/${noteId}?${params.toString()}`,
      {
        method: 'DELETE',
      },
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to delete note');
    }

    return true;
  },

  // Get all notes for an organization with optional filtering
  async getNotesByOrganization(
    accessToken: string | null,
    setUserAndToken: (user: any, token: string) => void,
    entityType?: 'customer' | 'invoice',
    skip: number = 0,
    limit: number = 100
  ): Promise<NotesListResponse> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (entityType) {
      params.append('entity_type', entityType);
    }

    const response = await authFetch(
      `${API_BASE}/notes?${params.toString()}`,
      {},
      accessToken,
      setUserAndToken
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to fetch notes');
    }

    const result = await response.json();
    return result.data;
  },
};
